// src/pages/Dashboard.js
import React, { useState, useEffect} from 'react';
import { useAuth } from '../hooks/useAuth';
import { db } from '../lib/firebase';
import {
  collection, query, orderBy, limit,
  where, Timestamp, onSnapshot,
} from 'firebase/firestore';
import { createTable, PLAN_LIMITS } from '../lib/MerchantModel';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, BarChart, Bar,
} from 'recharts';
import {
  Users, Receipt, TrendingUp, Clock,
  ArrowUpRight, ArrowDownRight, QrCode,
  Plus, Download
} from 'lucide-react';
import QRCode from 'qrcode';

// ─── Stat card ────────────────────────────────────────────────────────────────
const StatCard = ({ label, value, sub, icon: Icon, trend, color = '#4000E4' }) => (
  <div style={{
    background: '#fff', borderRadius: '16px', padding: '24px',
    border: '1px solid rgba(0,0,0,0.06)', boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
  }}>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
      <div style={{
        width: '40px', height: '40px', background: `${color}12`,
        borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <Icon size={18} color={color} />
      </div>
      {trend !== undefined && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: '4px',
          fontSize: '12px', fontWeight: 600,
          color: trend >= 0 ? '#16a34a' : '#dc2626',
        }}>
          {trend >= 0 ? <ArrowUpRight size={14} /> : <ArrowDownRight size={14} />}
          {Math.abs(trend)}%
        </div>
      )}
    </div>
    <div style={{
      fontSize: '28px', fontWeight: 700, color: '#1a1a1a',
      marginBottom: '4px', fontFamily: "'Cormorant Garamond', serif",
    }}>
      {value}
    </div>
    <div style={{ fontSize: '13px', fontWeight: 600, color: '#555', marginBottom: '2px' }}>{label}</div>
    {sub && <div style={{ fontSize: '11px', color: '#aaa' }}>{sub}</div>}
  </div>
);

// ─── Chart tooltip ────────────────────────────────────────────────────────────
const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{
      background: '#fff', border: '1px solid rgba(0,0,0,0.08)',
      borderRadius: '10px', padding: '10px 14px',
      boxShadow: '0 4px 16px rgba(0,0,0,0.08)', fontSize: '13px',
    }}>
      <div style={{ color: '#888', marginBottom: '4px' }}>{label}</div>
      {payload.map((p, i) => (
        <div key={i} style={{ color: p.color, fontWeight: 600 }}>
          {p.name}: {p.name === 'revenue' ? `$${p.value}` : p.value}
        </div>
      ))}
    </div>
  );
};

// ─── QR download helper ───────────────────────────────────────────────────────
const downloadQr = async (qrUrl, label) => {
  const dataUrl = await QRCode.toDataURL(qrUrl, {
    width: 512, margin: 2,
    color: { dark: '#1a1a1a', light: '#F7F4EF' },
  });
  const a = document.createElement('a');
  a.href = dataUrl;
  a.download = `checkie-${label.replace(/\s+/g, '-').toLowerCase()}.png`;
  a.click();
};

// ─── Table row ────────────────────────────────────────────────────────────────
const TableRow = ({ table }) => (
  <div style={{
    display: 'grid',
    gridTemplateColumns: '1fr 2fr 80px 80px',
    gap: '12px',
    padding: '14px 0',
    borderBottom: '1px solid #f5f5f5',
    alignItems: 'center',
  }}>
    <div style={{ fontSize: '13px', fontWeight: 600, color: '#1a1a1a' }}>
      {table.label}
    </div>
    <div style={{ fontSize: '12px', color: '#aaa', fontFamily: 'monospace' }}>
      {table.qrUrl}
    </div>
    {/* Active badge */}
    <div>
      <span style={{
        fontSize: '10px', fontWeight: 600, letterSpacing: '0.06em',
        textTransform: 'uppercase', padding: '3px 10px', borderRadius: '20px',
        background: table.active ? '#f0fdf4' : '#fef2f2',
        color: table.active ? '#16a34a' : '#dc2626',
      }}>
        {table.active ? 'Active' : 'Off'}
      </span>
    </div>
    {/* Download QR */}
    <button
      onClick={() => downloadQr(table.qrUrl, table.label)}
      style={{
        display: 'flex', alignItems: 'center', gap: '6px',
        padding: '6px 12px', borderRadius: '8px',
        background: 'transparent', border: '1px solid rgba(64,0,228,0.2)',
        color: '#4000E4', fontSize: '12px', fontWeight: 600,
        cursor: 'pointer', transition: 'all 0.15s',
      }}
      onMouseEnter={e => e.currentTarget.style.background = 'rgba(64,0,228,0.06)'}
      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
    >
      <Download size={13} /> QR
    </button>
  </div>
);

// ─── Add table modal ──────────────────────────────────────────────────────────
const AddTableModal = ({ merchantId, onClose, onAdded }) => {
  const [label, setLabel] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleAdd = async () => {
    if (!label.trim()) { setError('Enter a table name'); return; }
    setLoading(true);
    try {
      const table = await createTable(merchantId, label.trim());
      onAdded(table);
      onClose();
    } catch (e) {
      setError(e.message);
    }
    setLoading(false);
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 100, padding: '24px',
    }}>
      <div style={{
        background: '#fff', borderRadius: '20px', padding: '32px',
        width: '100%', maxWidth: '380px',
        boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
      }}>
        <h3 style={{
          fontFamily: "'Cormorant Garamond', serif",
          fontSize: '22px', fontWeight: 500,
          color: '#1a1a1a', marginBottom: '6px',
        }}>
          Add a Table
        </h3>
        <p style={{ fontSize: '13px', color: '#999', marginBottom: '24px' }}>
          We'll generate a unique QR code for this table.
        </p>
        <label style={labelStyle}>Table Name</label>
        <input
          style={{
            width: '100%', padding: '12px 16px',
            border: '1px solid rgba(0,0,0,0.12)', borderRadius: '12px',
            fontSize: '14px', color: '#1a1a1a', outline: 'none',
            fontFamily: "'Outfit', sans-serif", marginBottom: '8px',
            boxSizing: 'border-box',
          }}
          placeholder="e.g. Table 4, Bar Seat 2, Patio A"
          value={label}
          onChange={e => setLabel(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && handleAdd()}
          autoFocus
        />
        {error && (
          <p style={{ fontSize: '12px', color: '#dc2626', marginBottom: '12px' }}>{error}</p>
        )}
        <div style={{ display: 'flex', gap: '10px', marginTop: '8px' }}>
          <button
            onClick={onClose}
            style={{
              flex: 1, padding: '11px', borderRadius: '10px',
              border: '1px solid rgba(0,0,0,0.1)', background: 'transparent',
              fontSize: '13px', fontWeight: 600, color: '#888', cursor: 'pointer',
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleAdd}
            disabled={loading}
            style={{
              flex: 2, padding: '11px', borderRadius: '10px',
              border: 'none', background: '#4000E4', color: '#fff',
              fontSize: '13px', fontWeight: 600, cursor: 'pointer',
              opacity: loading ? 0.6 : 1,
            }}
          >
            {loading ? 'Creating...' : 'Create Table & QR'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Main Dashboard ───────────────────────────────────────────────────────────
export default function Dashboard() {
  const { merchant } = useAuth();
  console.log('merchant:', merchant);
  const [sessions, setSessions]     = useState([]);
  const [tables, setTables]         = useState([]);
  const [loading, setLoading]       = useState(true);
  const [tablesLoading, setTablesLoading] = useState(true);
  const [showAddTable, setShowAddTable]   = useState(false);
  const [activeTab, setActiveTab]         = useState('overview'); // 'overview' | 'tables'

  // ── Load sessions (last 30 days) ──
  useEffect(() => {
    if (!merchant?.id) return;
    const thirtyDaysAgo = Timestamp.fromDate(
      new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)
    );
    const q = query(
      collection(db, 'merchants', merchant.id, 'sessions'), // ← merchants not merchants
      where('createdAt', '>=', thirtyDaysAgo),
      orderBy('createdAt', 'desc'),
      limit(100),
    );
    // Real-time so dashboard updates live as guests complete sessions
    const unsub = onSnapshot(q, snap => {
      setSessions(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setLoading(false);
    }, () => setLoading(false));
    return unsub;
  }, [merchant?.id]);

  // ── Load tables ──
  useEffect(() => {
    if (!merchant?.id) return;
    const q = query(
      collection(db, 'merchants', merchant.id, 'tables'),
      orderBy('createdAt', 'asc'),
    );
    const unsub = onSnapshot(q, snap => {
      setTables(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setTablesLoading(false);
    }, () => setTablesLoading(false));
    return unsub;
  }, [merchant?.id]);

  const handleTableAdded = (table) => {
    setTables(prev => [...prev, table]);
  };

  // ── Compute stats ──
  const totalSessions  = sessions.length;
  const totalRevenue   = sessions.reduce((s, x) => s + (x.totalAmount || 0), 0);
  const avgPartySize   = totalSessions > 0
    ? (sessions.reduce((s, x) => s + (x.partySize || 0), 0) / totalSessions).toFixed(1)
    : '—';
  const avgCheck       = totalSessions > 0
    ? `$${(totalRevenue / totalSessions).toFixed(2)}`
    : '—';

  // ── Chart — last 7 days ──
  const chartData = Array.from({ length: 7 }, (_, i) => {
    const date = new Date();
    date.setDate(date.getDate() - (6 - i));
    
    const dayStr = date.toDateString();
    const day = sessions.filter(s => s.createdAt?.toDate?.()?.toDateString() === dayStr);
    return {
      day: date.toLocaleDateString('en-US', { weekday: 'short' }),
      splits:  day.length,
      revenue: parseFloat(day.reduce((s, x) => s + (x.totalAmount || 0), 0).toFixed(2)),
    };
  });

  const hour = new Date().getHours();
  const greeting = hour < 12 ? 'morning' : hour < 17 ? 'afternoon' : 'evening';

  const planLimit   = PLAN_LIMITS[merchant?.plan || 'silver'].sessionsPerMonth;
  const usagePct    = planLimit === Infinity ? 0 : Math.min((totalSessions / planLimit) * 100, 100);
  const nearLimit   = totalSessions >= planLimit * 0.9;

  return (
    <div style={{ fontFamily: "'Outfit', sans-serif" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,400;0,500;1,400&family=Outfit:wght@300;400;500;600;700&display=swap');
      `}</style>

      {showAddTable && merchant?.id && (
        <AddTableModal
          merchantId={merchant.id}
          onClose={() => setShowAddTable(false)}
          onAdded={handleTableAdded}
        />
      )}

      {/* ── Page header ── */}
      <div style={{ marginBottom: '32px' }}>
        <h1 style={{
          fontFamily: "'Cormorant Garamond', serif",
          fontSize: '32px', fontWeight: 500,
          color: '#1a1a1a', marginBottom: '6px',
        }}>
          Good {greeting} 👋
        </h1>
        <p style={{ fontSize: '14px', color: '#888' }}>
          Here's what's happening at{' '}
          <strong style={{ color: '#1a1a1a' }}>{merchant?.name || 'your merchant'}</strong> this month.
        </p>
      </div>

      {/* ── Tab nav ── */}
      <div style={{
        display: 'flex', gap: '4px', marginBottom: '28px',
        background: '#f5f5f5', borderRadius: '12px',
        padding: '4px', width: 'fit-content',
      }}>
        {[
          { key: 'overview', label: 'Overview', icon: TrendingUp },
          { key: 'tables',   label: 'Tables & QR Codes', icon: QrCode },
        ].map(t => (
          <button
            key={t.key}
            onClick={() => setActiveTab(t.key)}
            style={{
              display: 'flex', alignItems: 'center', gap: '6px',
              padding: '8px 16px', borderRadius: '9px', border: 'none',
              fontSize: '13px', fontWeight: 600, cursor: 'pointer',
              background: activeTab === t.key ? '#4000E4' : 'transparent',
              color: activeTab === t.key ? '#fff' : '#888',
              transition: 'all 0.2s',
            }}
          >
            <t.icon size={14} />
            {t.label}
          </button>
        ))}
      </div>

      {/* ════════════════════════════════════════════════════
          OVERVIEW TAB
      ════════════════════════════════════════════════════ */}
      {activeTab === 'overview' && (
        <>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '60px', color: '#aaa', fontSize: '14px' }}>
              Loading your data...
            </div>
          ) : (
            <>
              {/* Stats grid */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: '16px', marginBottom: '32px',
              }}>
                <StatCard label="Total Splits"    value={totalSessions}          sub="Last 30 days"       icon={Receipt}    color="#4000E4" />
                <StatCard label="Avg Party Size"  value={avgPartySize}           sub="People per split"   icon={Users}      color="#7c3aed" />
                <StatCard label="Avg Check Size"  value={avgCheck}               sub="Per session"        icon={TrendingUp} color="#059669" />
                <StatCard label="Time Saved"      value={`~${totalSessions * 8} min`} sub="Est. 8 min/group" icon={Clock}  color="#d97706" />
              </div>

              {/* Plan usage — show for silver and gold, not platinum */}
              {merchant?.plan !== 'platinum' && (
                <div style={{
                  background: '#fff', borderRadius: '16px', padding: '20px 24px',
                  border: nearLimit ? '1px solid #fecaca' : '1px solid rgba(0,0,0,0.06)',
                  marginBottom: '24px',
                  display: 'flex', alignItems: 'center', gap: '20px', flexWrap: 'wrap',
                }}>
                  <div style={{ flex: 1, minWidth: '200px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                      <span style={{ fontSize: '13px', fontWeight: 600, color: '#555' }}>
                        {PLAN_LIMITS[merchant?.plan]?.label || 'Silver'} Plan Usage
                      </span>
                      <span style={{ fontSize: '13px', color: nearLimit ? '#dc2626' : '#888' }}>
                        {totalSessions} / {planLimit} sessions
                      </span>
                    </div>
                    <div style={{ height: '6px', background: '#f0f0f0', borderRadius: '99px', overflow: 'hidden' }}>
                      <div style={{
                        height: '100%', width: `${usagePct}%`,
                        background: nearLimit ? '#dc2626' : '#4000E4',
                        borderRadius: '99px', transition: 'width 0.5s ease',
                      }} />
                    </div>
                  </div>
                  <button style={{
                    padding: '8px 20px', background: '#4000E4', color: '#fff',
                    border: 'none', borderRadius: '10px', fontSize: '13px',
                    fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap',
                  }}>
                    Upgrade Plan →
                  </button>
                </div>
              )}

              {/* Charts */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
                gap: '16px', marginBottom: '32px',
              }}>
                <div style={{ background: '#fff', borderRadius: '16px', padding: '24px', border: '1px solid rgba(0,0,0,0.06)' }}>
                  <div style={{ fontSize: '14px', fontWeight: 600, color: '#555', marginBottom: '4px' }}>Splits per Day</div>
                  <div style={{ fontSize: '11px', color: '#aaa', marginBottom: '20px' }}>Last 7 days</div>
                  <ResponsiveContainer width="100%" height={160}>
                    <AreaChart data={chartData}>
                      <defs>
                        <linearGradient id="splitGrad" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%"  stopColor="#4000E4" stopOpacity={0.15} />
                          <stop offset="95%" stopColor="#4000E4" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="day" tick={{ fontSize: 11, fill: '#aaa' }} axisLine={false} tickLine={false} />
                      <YAxis                tick={{ fontSize: 11, fill: '#aaa' }} axisLine={false} tickLine={false} />
                      <Tooltip content={<CustomTooltip />} />
                      <Area type="monotone" dataKey="splits" name="splits" stroke="#4000E4" strokeWidth={2} fill="url(#splitGrad)" />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>

                <div style={{ background: '#fff', borderRadius: '16px', padding: '24px', border: '1px solid rgba(0,0,0,0.06)' }}>
                  <div style={{ fontSize: '14px', fontWeight: 600, color: '#555', marginBottom: '4px' }}>Check Totals ($)</div>
                  <div style={{ fontSize: '11px', color: '#aaa', marginBottom: '20px' }}>Last 7 days</div>
                  <ResponsiveContainer width="100%" height={160}>
                    <BarChart data={chartData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="day" tick={{ fontSize: 11, fill: '#aaa' }} axisLine={false} tickLine={false} />
                      <YAxis                tick={{ fontSize: 11, fill: '#aaa' }} axisLine={false} tickLine={false} />
                      <Tooltip content={<CustomTooltip />} />
                      <Bar dataKey="revenue" name="revenue" fill="#4000E4" radius={[4, 4, 0, 0]} opacity={0.8} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Recent sessions */}
              <div style={{ background: '#fff', borderRadius: '16px', padding: '24px', border: '1px solid rgba(0,0,0,0.06)' }}>
                <div style={{ fontSize: '14px', fontWeight: 600, color: '#555', marginBottom: '20px' }}>Recent Sessions</div>
                {sessions.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '40px', color: '#bbb' }}>
                    <div style={{ fontSize: '32px', marginBottom: '12px' }}>🍽</div>
                    <div style={{ fontSize: '14px', fontWeight: 600, marginBottom: '6px', color: '#888' }}>No sessions yet</div>
                    <div style={{ fontSize: '13px' }}>Sessions will appear here once guests start splitting checks at your tables.</div>
                  </div>
                ) : (
                  <div>
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: '1fr 1fr 1fr 1fr 80px',
                      gap: '12px', padding: '0 0 10px',
                      borderBottom: '1px solid #f0f0f0', marginBottom: '12px',
                    }}>
                      {['Table', 'Party Size', 'Check Total', 'Time', 'Status'].map(h => (
                        <div key={h} style={{ fontSize: '11px', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#aaa' }}>{h}</div>
                      ))}
                    </div>
                    {sessions.slice(0, 5).map(s => (
                      <div key={s.id} style={{
                        display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr 80px',
                        gap: '12px', padding: '12px 0',
                        borderBottom: '1px solid #fafafa', alignItems: 'center',
                      }}>
                        <div style={{ fontSize: '13px', fontWeight: 600, color: '#1a1a1a' }}>{s.tableLabel || s.tableId || '—'}</div>
                        <div style={{ fontSize: '13px', color: '#555' }}>{s.partySize || '—'} people</div>
                        <div style={{ fontSize: '13px', color: '#555' }}>{s.totalAmount ? `$${s.totalAmount.toFixed(2)}` : '—'}</div>
                        <div style={{ fontSize: '12px', color: '#aaa' }}>
                          {s.createdAt?.toDate?.()?.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }) || '—'}
                        </div>
                        <span style={{
                          fontSize: '10px', fontWeight: 600, letterSpacing: '0.06em',
                          textTransform: 'uppercase', padding: '3px 10px', borderRadius: '20px',
                          background: s.status === 'completed' ? '#f0fdf4' : '#fefce8',
                          color: s.status === 'completed' ? '#16a34a' : '#ca8a04',
                        }}>
                          {s.status || 'open'}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </>
      )}

      {/* ════════════════════════════════════════════════════
          TABLES & QR CODES TAB
      ════════════════════════════════════════════════════ */}
      {activeTab === 'tables' && (
        <div>
          {/* Header row */}
          <div style={{
            display: 'flex', alignItems: 'center',
            justifyContent: 'space-between', marginBottom: '20px',
          }}>
            <div>
              <div style={{ fontSize: '14px', fontWeight: 600, color: '#1a1a1a' }}>
                {tables.length} {tables.length === 1 ? 'Table' : 'Tables'}
              </div>
              <div style={{ fontSize: '12px', color: '#aaa' }}>
                Each table has a unique QR code. Guests scan it to split the check.
              </div>
            </div>
            <button
              onClick={() => setShowAddTable(true)}
              style={{
                display: 'flex', alignItems: 'center', gap: '8px',
                padding: '10px 18px', borderRadius: '12px',
                border: 'none', background: '#4000E4', color: '#fff',
                fontSize: '13px', fontWeight: 600, cursor: 'pointer',
                transition: 'opacity 0.2s',
              }}
              onMouseEnter={e => e.currentTarget.style.opacity = '0.85'}
              onMouseLeave={e => e.currentTarget.style.opacity = '1'}
            >
              <Plus size={15} /> Add Table
            </button>
          </div>

          {/* Table list */}
          <div style={{
            background: '#fff', borderRadius: '16px', padding: '24px',
            border: '1px solid rgba(0,0,0,0.06)',
          }}>
            {tablesLoading ? (
              <div style={{ textAlign: 'center', padding: '40px', color: '#aaa', fontSize: '14px' }}>
                Loading tables...
              </div>
            ) : tables.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '48px 24px' }}>
                <div style={{
                  width: '56px', height: '56px', background: 'rgba(64,0,228,0.08)',
                  borderRadius: '16px', display: 'flex', alignItems: 'center',
                  justifyContent: 'center', margin: '0 auto 16px',
                }}>
                  <QrCode size={24} color="#4000E4" />
                </div>
                <div style={{ fontSize: '15px', fontWeight: 600, color: '#1a1a1a', marginBottom: '6px' }}>
                  No tables yet
                </div>
                <div style={{ fontSize: '13px', color: '#aaa', marginBottom: '20px' }}>
                  Add your first table to generate a QR code your guests can scan.
                </div>
                <button
                  onClick={() => setShowAddTable(true)}
                  style={{
                    padding: '10px 20px', borderRadius: '10px',
                    border: 'none', background: '#4000E4', color: '#fff',
                    fontSize: '13px', fontWeight: 600, cursor: 'pointer',
                  }}
                >
                  Add Your First Table
                </button>
              </div>
            ) : (
              <>
                {/* Column headers */}
                <div style={{
                  display: 'grid', gridTemplateColumns: '1fr 2fr 80px 80px',
                  gap: '12px', paddingBottom: '10px',
                  borderBottom: '1px solid #f0f0f0', marginBottom: '4px',
                }}>
                  {['Table', 'QR URL', 'Status', 'Download'].map(h => (
                    <div key={h} style={{
                      fontSize: '11px', fontWeight: 600,
                      letterSpacing: '0.08em', textTransform: 'uppercase', color: '#aaa',
                    }}>{h}</div>
                  ))}
                </div>
                {tables.map(t => <TableRow key={t.id} table={t} />)}
              </>
            )}
          </div>

          {/* How it works explainer */}
          <div style={{
            background: 'rgba(64,0,228,0.04)', borderRadius: '16px',
            padding: '24px', marginTop: '20px',
            border: '1px solid rgba(64,0,228,0.1)',
          }}>
            <div style={{ fontSize: '13px', fontWeight: 700, color: '#4000E4', marginBottom: '12px', letterSpacing: '0.05em', textTransform: 'uppercase' }}>
              How It Works
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {[
                ['1', 'Print the QR code and place it on the table (tent card, sticker, or laminated card).'],
                ['2', 'Server drops the physical receipt on the table as normal — nothing changes for your staff.'],
                ['3', 'Guests scan the QR with their camera app. No download required.'],
                ['4', 'The Checkie web app opens. First guest photographs the receipt. AI parses it instantly.'],
                ['5', 'Everyone assigns their items and pays via Venmo, CashApp, or Zelle. Table is clear in minutes.'],
              ].map(([n, text]) => (
                <div key={n} style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                  <div style={{
                    width: '22px', height: '22px', borderRadius: '50%',
                    background: '#4000E4', color: '#fff',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '11px', fontWeight: 700, flexShrink: 0, marginTop: '1px',
                  }}>{n}</div>
                  <p style={{ fontSize: '13px', color: '#555', margin: 0, lineHeight: 1.5 }}>{text}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const labelStyle = {
  display: 'block', fontSize: '11px', fontWeight: 600,
  letterSpacing: '0.1em', textTransform: 'uppercase',
  color: '#888', marginBottom: '6px',
};